import { Util } from '../util';
import { Control } from '../control';
import { CustomCode } from '../custom-code';
import { Auth } from '../auth';
import { Schema } from '../schema';
import { BizData } from '../data';
import { Filter } from '../filter';
import { Operate } from '../operate';
import { Automation } from '../automation';
import { SmartAction } from '../smart-action';

export namespace View {
    /** 系统可用的视图类型 */
    export enum ViewMode {
        List = 0,
        Calendar = 1,
        Timeline = 2,
        Gantt = 4,
        Map = 5,
        Hierarchy = 6,
        Card = 7,
        Kanban = 8,
        MobileGrid = 9,
        Form = 100,
    }

    /** 列表页模式 */
    export enum ListPageMode {
        /** 平台提供的列表页 */
        Platform = 0,
        /** 自定义代码列表页 */
        CustomCode = 1,
    }

    /** 视图类型对应的配置key(准备弃用 groupTree用到) */
    export enum ViewModeKey {
        List = 'list',
        Calendar = 'calendar',
        Timeline = 'timeline',
        Gantt = 'gantt',
        Map = 'map',
        Hierarchy = 'hierarchy',
        Card = 'card',
        Kanban = 'kanban',
    }

    /** 可查看的组织数据范围类型 */
    export enum ViewScopeType {
        ViewOwn = 1,
        ViewOwnDept = 2,
        ViewAll = 3,
    }

    /** 排序维度 */
    export enum SortDirection {
        /** 升序 */
        Asc = 0,
        /** 降序 */
        Desc = 1,
    }

    /** 自定义代码类型 */
    export type ICustomCodeValue = {
        Value: string;
        Color: number;
        SchemaCode: string;
        DisplayListLinkOnForm?: boolean;
        ObjectId?: string;
    };

    /** 视图排序配置 */
    export type TSortConfig = {
        /** 排序字段 */
        sortBy: string;
        /** 排序方向 */
        sortDirection: SortDirection;
    };

    /** 列表视图排序配置 */
    export type SortCollection = {
        /** 排序字段 */
        itemName: string | undefined;
        /** 排序方向 */
        direction: SortDirection;
    };

    /** 自定义代码 列表配置 */
    export type CustomViewSetting = {
        isSupport: boolean;
        type: Util.Device;
        id: string;
        templates: CustomViewSettingTemplate[];
    };

    /** 自定义代码 列表配置模板 */
    export type CustomViewSettingTemplate = {
        title: string;
        id: string;
        template: string;
        isDefault: boolean;
    };

    /** 视图默认行高(列表视图、层级视图) */
    export enum ViewRowHeight {
        /** 紧凑(152迭代去除) */
        Compact = 1,
        /** 普通/低(后端不想升级数据，默认返回的是0) */
        Default = 0,
        /** 中 */
        Medium = 2,
        /** 高 */
        Large = 3,
        /** 超高 */
        HIGH = 4,
    }

    /** 日历视图时间维度 */
    export enum CalendarTimeMode {
        /** 天 */
        Day = 1,
        /** 周 */
        Week = 2,
        /** 月 */
        Month = 3,
    }

    /** 日历视图视图类型 */
    export enum CalendarViewType {
        /** 月视图 */
        MONTH_VIEW = 'month',
        /** 周视图 */
        WEEK_VIEW = 'week',
        /** 日视图 */
        DAY_VIEW = 'day',
    }

    /** 颜色规则(甘特图) */
    export interface IColorRule {
        colorTheme: number;
        colors: IColor;
        rule: IRule;
    }

    /** 颜色配置(甘特图) */
    export interface IColor {
        left: string;
        right: string;
        borderColor: string;
        textColor: string;
    }

    /** 规则配置(甘特图) */
    export interface IRule {
        formula: string;
        formulaText: string;
        fields: string[];
    }

    /** 甘特图属性权限 */
    export interface IPropertiesAuth {
        propertyName: string;
        visible: boolean;
        editable: boolean;
    }

    /** 看板张开与收起状态 */
    export interface BoardCollapsed {
        key: string;
        collapsed: boolean;
    }
    /** 看板张开与收起状态----分组多字段 */
    export interface BoardCollapsedCache {
        [key: string]: BoardCollapsed[];
    }
    export const KanbanViewNoDataGroupId = 'noGroup';
    export interface BoardCacheItem {
        key: string;
        visible: boolean;
        collapsed: boolean;
    }
    /** 看板张开与收起状态----分组多字段 */
    export interface BoardCache {
        [key: string]: {
            visible: boolean;
            key: string;
            collapsed: boolean;
        }[];
    }

    /** 甘特图时间维度 */
    export enum GanttScaleType {
        /** 小时 */
        Hour = 1,
        /** 天 */
        Day = 2,
        /** 周 */
        Week = 3,
        /** 月 */
        Month = 4,
        /** 季度 */
        Quarter = 5,
        /** 年 */
        Year = 6,
    }

    /** 层级视图显示方式 */
    export enum HierarchyCss {
        List = 1,
        Card = 2,
    }

    /** 卡片视图显示位置 */
    export enum ImagePosition {
        Left = 'left',
        Top = 'top',
    }

    /** 卡片视图显示模式 */
    export enum ImageDisplay {
        /** 原图居中 */
        CenterOriginal = 'centerOriginal',
        /** 原图填充 */
        FillOriginal = 'fillOriginal',
        /** 拉升填充 */
        StretchFill = 'stretchFill',
    }

    /** 视图按钮类型 */
    export const enum ActionType {
        /** 系统按钮 */
        System,
        /** 自定义按钮 */
        Custom,
        /** 批量编辑 */
        Batch,
    }

    /** 自定义按钮执行类型 */
    export const enum ExecuteType {
        None = 0,
        /** 执行自动化 */
        Automation = 1,
        /** 执行自定义代码 */
        CustomCode = 2,
        /** 执行平台集成 */
        PlatformIntegration = 3,
        /** 新增数据 */
        NewData = 4,
    }

    /** 系统内置按钮key */
    export enum SystemActionKey {
        Create = 'Create',
        Remove = 'Remove',
        Remove_Form = 'Remove_Form',
        Remove_List = 'Remove_List',
        Import = 'Import',
        Export = 'Export',
        ExportToWXWorkspace = 'ExportToWXWorkspace',
        PrintQrCode = 'PrintQrCode',
        Print = 'Print',
        Print_Form = 'Print_Form',
        Print_List = 'Print_List',
        PrintQrCode_Inner = 'PrintQrCode_Inner',
        PrintQrCode_Outer = 'PrintQrCode_Outer',
        Save = 'Save',
        Copy = 'Copy',
        Submit = 'Submit',
    }

    // 系统按钮默认名称
    export const SystemActionNameMap = {
        [SystemActionKey.Create]: '新增',
        [SystemActionKey.Remove]: '删除',
        [SystemActionKey.Remove_List]: '删除',
        [SystemActionKey.Remove_Form]: '删除',
        [SystemActionKey.Import]: '导入',
        [SystemActionKey.Export]: '导出',
        [SystemActionKey.PrintQrCode]: '打印二维码',
        [SystemActionKey.Print]: '打印',
        [SystemActionKey.Print_Form]: '打印',
        [SystemActionKey.Print_List]: '打印',
        [SystemActionKey.PrintQrCode_Inner]: '打印二维码',
        [SystemActionKey.PrintQrCode_Outer]: '打印二维码',
        [SystemActionKey.Save]: '暂存',
        [SystemActionKey.Copy]: '复制',
        [SystemActionKey.Submit]: '提交',
    };

    /** 视图元数据 */
    export interface IViewProperty extends Schema.IProperty {
        /** 子组件 */
        childProperties?: Schema.IProperty[];

        /** 是否子表控件 */
        isChild?: boolean;
    }

    /** 视图按钮结构 */
    export interface IAction {
        /** 按钮编码 */
        actionCode: string;
        /** 按钮名称 */
        displayName: string;
        /** 旧版按钮编码 */
        oldCode?: string;
        /** 按钮图标 */
        icon: string;
        /** 是否自定义打印 */
        isCustomPrint?: boolean;
        // /** 是否打印 */
        isPrintAction?: boolean;
        // /** 按钮类型 */
        // actionType?: ActionType;
        // /** 执行配置 */
        // executeConfig?: IExecuteConfig;
        /** 是否合并执行 */
        mergeBizData?: boolean;
        executeSetting?: SmartAction.IExecuteSetting;
        selectable?: boolean;
        styleType?: SmartAction.StyleType;
        confirmInfo?: {
            title: string;
            detail: string;
        } | null;
        submitRule?: {
            state: boolean;
            errorMessage: string;
            ruleString: string;
            submitRuleActionType: SmartAction.SubmitRuleActionType;
        };
        behindCode?: string;
        frontendCode?: string;
        validateFieldConsistency?: SmartAction.IValidateFieldConsistency; // 一致性校验配置
    }

    /** 自定义按钮执行配置 */
    export interface IExecuteConfig {
        /** 执行内容 */
        executeContent: ExecuteType;
        /** 自动化模板编码 */
        automationTemplateId: string;
        /** 确认信息 */
        confirmInfo: IConfirmInfo | null;
        /** 可编辑字段 */
        editableProperties?: string[];
        /** 单步执行还是合并执行 */
        mergeBizData?: boolean;
        /** 考虑后期扩展，采用了json字符串格式 */
        pluginActionParam?: string;
        /** 数据校验 */
        submitRule?: {
            state: boolean;
            errorMessage: string;
            ruleString: string;
            rule?: Filter.IFilterItem[][];
        };
        /** 新增关联表单编码 */
        associationSchemaCode?: string;
        automationParamSetting?: {
            controls: Automation.V3.ICustomArgControl[];
        };
    }

    /** 自定义按钮二次确认内容结构 */
    export interface IConfirmInfo {
        title: string;
        detail: string;
    }

    /** 级联筛选配置 */
    export type ICascadeFilterRule = {
        /** 当前表单字段 */
        curFormFieldCode?: string;
        /** 目标表单字段 */
        targetFormFieldCode?: string;
        /** 当前表单控件key */
        curFormFieldControlKey?: string;
    };

    /** 视图筛选项 */
    export interface IQueryItem {
        /** 是否允许为空 */
        allowNull: boolean;
        /** 默认值 */
        defaultValue: unknown;
        /** 筛选配置 */
        filterConfigs: Array<Record<string, unknown>>;
        /** 筛选值 */
        filterValue: string | undefined;
        /** 组织类型 */
        organizationType: ViewScopeType;
        /** 属性名称 */
        propertyName: string;
        /** 是否可见 */
        visible: boolean;
        /** 是否默认展示(收起) */
        isDefault: boolean;
        /** 关联表单编码 */
        associationSchemaCode?: string;
        /** 可选值 */
        optionalValues?: unknown[];
        /** 日期时间模式 */
        dateTimeMode?: string;
        /** 表单控件类型 */
        formControlType?: number;
        /** 表单控件类型key */
        formControlTypeKey?: Control.FormControlKeys;
        /** 数据类型 */
        dataType?: Control.BizDataType;
        /** 操作符 */
        defaultOperator: BizData.Filter.ComparisonOperatorType;
    }

    /** 视图筛选配置项值 */
    export interface IQueryValueItem {
        propertyName: string;
        defaultValues: string[];
        defaultOperator: BizData.Filter.ComparisonOperatorType;
    }

    /** 视图字段权限 */
    export interface IFieldPermission {
        /** 字段名称 */
        code: string;
        /** 是否可见 */
        visible: boolean;
        /** 是否可编辑 */
        editable: boolean;
        /** 必填 */
        required: boolean;
    }

    /** 视图分组筛选项 */
    export interface IGroupQueryItem {
        /** 字段名称 */
        propertyName: string;
        /** 是否树形结构 */
        isTreeStructure?: boolean;
        /** 是否允许为空 */
        allowNull?: boolean;
        /** 默认值 */
        defaultValue?: unknown;
        /** 筛选配置 */
        filterConfigs?: Array<Record<string, unknown>>;
        /** 筛选值 */
        filterValue?: string | undefined;
        /** 组织类型 */
        organizationType?: ViewScopeType;
        /** 是否可见 */
        visible?: boolean;
        /** 是否默认展示(收起) */
        isDefault?: boolean;
    }

    /**
     * 格式化筛选项的筛选公式
     * @description 这个枚举是给统计分析使用的，和氚云请求列表数据筛选参数中的筛选公式不是一回事
     */
    export enum ComparisonFormula {
        /** 等于 */
        Equal = 'Equal',
        /** 不等于 */
        NotEqual = 'NotEqual',
        /** 开头为 */
        StartWith = 'StartWith',
        /** 包含 */
        In = 'In',
        /** 不包含 */
        NotIn = 'NotIn',
        /** 为空 */
        None = 'None',
        /** 不为空 */
        NotNone = 'NotNone',
        /** 模糊匹配 包含 */
        Match = 'Match',
        /** 模糊匹配 不包含 */
        NotMatch = 'NotMatch',
        /** 范围 */
        Range = 'Range',
        /** 大于 */
        Above = 'Above',
        /** 大于等于 */
        NotBelow = 'NotBelow',
        /** 小于 */
        Below = 'Below',
        /** 小于等于 */
        NotAbove = 'NotAbove',
        /** 动态筛选 */
        Dynamic = 'Dynamic',
        /** 自定义 */
        Custom = 'Custom',
    }

    /**
     * 格式化后的视图筛选项
     * @description 这个数据结构是给统计分析使用的
     */
    export type TFormattedQueryItem = {
        /** 筛选值 */
        value?: unknown[];
        /** 表单编码（字段属于某个子表时，该字段表示子表编码） */
        schemaCode?: string;
        /** 字段编码 */
        field?: string;
        /** 筛选公式 */
        formula?: ComparisonFormula;
        /** 且条件 */
        ands?: TFormattedQueryItem[];
        /** 或条件 */
        ors?: TFormattedQueryItem[];
    };

    /** 视图筛选条件 */
    export type FilterCriterion = {
        field: string;
        operator: BizData.Filter.ComparisonOperatorType;
        value: unknown;
    };

    /** 列表视图可见列配置 */
    export interface IListViewColumn {
        /** 字段编码 */
        propertyName: string;
        /** 字段是否可见 */
        visible: boolean;
        /** 字段显示名称 */
        displayName?: string;
        /** 是否子表字段 */
        isChild?: boolean;
        /** 子表字段内字段配置 */
        childColumns?: IListViewColumn[];
        /** 前端写入的控件类型 */
        formControlTypeKey?: Control.FormControlKeys;
        /** 列宽 */
        width?: number;
        /** 固定列 */
        fixed?: boolean;
    }

    export interface IHierarchyViewColumn {
        /** 字段编码 */
        code: string;
        /** 列宽 */
        width?: number;
        /** 固定列 */
        fixed?: boolean;
    }

    /** 列表配置 */
    export interface IListViewSetting {
        /** 排序字段 */
        sortBy: string;
        /** 排序方向 */
        sortDirection: SortDirection;
        /** 图标字段 */
        iconField?: string;
        /** 默认显示子表字段 */
        defaultDisplayChildField?: string;
        /** 列表显示字段 */
        displayProperties?: string[];
        /** 列表字段配置 */
        columns?: IListViewColumn[];
        /** 默认排序规则 */
        sortByCollection?: SortCollection[];
        /** 操作列 */
        operationColumn?: SmartAction.IOperationColumn;
        /** 默认行高 */
        defaultRowHeight?: ViewRowHeight;
    }

    /** 日历视图配置 */
    export interface ICalendarViewSetting {
        /** 开始时间字段 */
        startField?: string;
        /** 结束时间字段 */
        endField?: string;
        /** 时间模式 */
        timeMode?: CalendarTimeMode;
    }

    /** 甘特图视图配置 */
    export interface IGanttViewSetting {
        /** 排序字段 */
        sortBy: string;
        /** 排序方向 */
        sortDirection: SortDirection;
        /** 开始时间字段 */
        startField?: string;
        /** 结束时间字段 */
        endField?: string;
        /** 关联层级关系字段(关联控件) */
        levelRelation?: string;
        /** 关联依赖关系字段(关联多选控件) */
        dependency?: string;
        /** 甘特图刻度 */
        ganttScale?: GanttScaleType;
        /** 进度字段 */
        progressField?: string;
        /** 颜色规则 */
        colorRules: IColorRule[];
        /** 属性权限 */
        propertiesAuths: IPropertiesAuth[];
    }

    /** 时间线视图配置 */
    export interface ITimelineViewSetting {
        /** 排序方向 */
        sortDirection: SortDirection;
        /** 时间轴轴字段 */
        timelineAxis?: string;
    }

    /** 地图视图配置 */
    export interface IMapViewSetting {
        /** 排序字段 */
        sortBy: string;
        /** 排序方向 */
        sortDirection: SortDirection;
        /** 地理位置字段 */
        locationField?: string;
        /** 是否限制移动端数据展示范围 */
        isMobileNearbyData: boolean;
        /** 移动端数据展示范围 */
        isMobileNearbyDataRangeInMeters: number;
        /** 移动端是否按位置字段排序 */
        isMobileSortByLocation: boolean;
    }

    /** 层级视图配置 */
    export interface IHierarchyViewSetting {
        /** 排序字段 */
        sortKey: string;
        /** 排序方向 */
        sortDirection: SortDirection;
        /** 层级字段 */
        hierarchyField?: string;
        /** 显示方式 */
        css?: HierarchyCss;
        /** 显示字段 */
        displayProperties: string[];
        /** 默认行高 */
        defaultRowHeight?: ViewRowHeight;
        /** 列表字段配置 */
        columnSettings?: IHierarchyViewColumn[];
    }

    /** 卡片视图配置 */
    export interface ICardViewSetting {
        /** 排序字段 */
        sortKey: string;
        /** 排序方向 */
        sortDirection: SortDirection;
        /** 图片字段 */
        imagePropertyName?: string;
        /** 图片位置 */
        imagePosition?: ImagePosition;
        /** 图片显示方式 */
        imageDisplay?: ImageDisplay;
        /** 显示字段 */
        displayProperties: string[];
        /** 是否隐藏名称 */
        hideDisplayName: boolean;
        /** 操作列 */
        operationColumn?: SmartAction.IOperationColumn;
    }

    /** 看板视图配置 */
    export interface IKanbanViewSetting extends ICardViewSetting {
        groupKey?: string;
        defaultGroupKey?: string;
    }

    /** 表单视图配置 */
    export interface IFormViewSetting {
        /** 排序字段 */
        sortKey: string;
        /** 排序方向 */
        sortDirection: SortDirection;
        /** 显示字段 */
        displayProperties: string[];
        /** 是否开启左侧列表 */
        leftListOpen: boolean;
        /** 图片字段 */
        imagePropertyName?: string;
        /** 图片显示方式 */
        imageDisplay?: ImageDisplay;
    }

    /** 偏好集合 */
    export interface IPreferenceItem {
        /** id */
        Id: string;
        /** 名称 */
        Title: string;
        /** 配置类型 */
        SettingType: number;
        /** 排序 */
        Sort: number;
        /** 偏好是否可见 */
        Visible: boolean;
        /** 系统标识 */
        SystemKey?: string;
        /** 筛选范围 */
        ScopeType: Auth.DataScopeType;
        /** 偏好项编码 */
        ItemCode?: string;
        /** 偏好项配置 */
        Config?: Record<string, unknown>;
        /** 用户标识 */
        UserId?: string;
        /** 偏好项字段 */
        Fields?: string[];
        /** pageId */
        ViewPageId: string;
        /** 视图筛选条件 */
        FilterCriteria?: FilterCriterion[];
    }

    /**  更新看板视图 */
    export interface UpdateKanbanBiz {
        bizObjectId: string;
        actionName: string;
        postValue: {
            command: string;
            data: Record<string, string>;
        };
    }

    /** 系统按钮 */
    export const SYSTEM_ACTIONS = [
        SystemActionKey.Create,
        SystemActionKey.Import,
        SystemActionKey.Export,
        SystemActionKey.ExportToWXWorkspace,
        SystemActionKey.Remove,
        SystemActionKey.Remove_List,
        SystemActionKey.PrintQrCode,
        SystemActionKey.PrintQrCode_Inner,
        SystemActionKey.PrintQrCode_Outer,
        SystemActionKey.Print,
        SystemActionKey.Print_List,
    ];

    /** 筛选支持的控件类型 */
    export const FILTER_SUPPORT_TYPES = [
        Control.FormControlKeys.FormTextBox,
        Control.FormControlKeys.FormTextArea,
        Control.FormControlKeys.FormNumber,
        Control.FormControlKeys.FormDateTime,
        Control.FormControlKeys.FormDropDownList,
        Control.FormControlKeys.FormRadioButtonList,
        Control.FormControlKeys.FormCheckbox,
        Control.FormControlKeys.FormCheckboxList,
        Control.FormControlKeys.FormAreaSelect,
        Control.FormControlKeys.FormSeqNo,
        Control.FormControlKeys.FormUser,
        Control.FormControlKeys.FormDepartment,
        Control.FormControlKeys.FormMultiUser,
        Control.FormControlKeys.FormMultiDepartment,
        Control.FormControlKeys.FormOwner,
        Control.FormControlKeys.FormOwnerDepartment,
        Control.FormControlKeys.FormCreater,
        Control.FormControlKeys.FormCreatedTime,
        Control.FormControlKeys.FormModifiedTime,
        Control.FormControlKeys.FormQuery,
        Control.FormControlKeys.FormMultiQuery,
        Control.FormControlKeys.FormGridView,
        Control.FormControlKeys.FormStatus,
        Control.FormControlKeys.FormRollup,
        Control.FormControlKeys.FormFormula,
    ];

    /** 筛选支持的系统字段 */
    export const FILTER_SUPPORT_FIELDS = ['CreatedBy', 'OwnerId', 'OwnerDeptId', 'CreatedTime', 'ModifiedTime'];

    /** 筛选不支持的控件编码 */
    export const FILTER_EXCLUDE_FIELDS = ['ObjectId', 'Name', 'ModifiedBy', 'WorkflowInstanceId', 'ParentObjectId'];

    /** 排序支持的数据类型 */
    export const SORT_SUPPORT_DATA_TYPES = [
        Control.BizDataType.ShortString,
        Control.BizDataType.DateTime,
        Control.BizDataType.Int,
        Control.BizDataType.Bool,
        Control.BizDataType.Decimal,
        Control.BizDataType.Double,
    ];

    /** 支持排序的系统字段 */
    export const SORT_SUPPORT_FIELDS = ['CreatedTime', 'ModifiedTime'];

    /** 不支持排序的系统字段 */
    export const SORT_NOT_SUPPORT_FIELDS = ['WorkflowActivityName', 'WorkflowParticipant'];

    /** 列统计支持的数据类型 */
    export const STAT_SUPPORT_DATA_TYPES = [
        Control.BizDataType.SingleParticipant,
        Control.BizDataType.MultiParticipant,
        Control.BizDataType.String,
        Control.BizDataType.ShortString,
        Control.BizDataType.StringArray,
        Control.BizDataType.DateTime,
        Control.BizDataType.Double,
        Control.BizDataType.Address,
        Control.BizDataType.Association,
        Control.BizDataType.AssociationArray,
    ];

    /** 默认空值 */
    export const NULL_VALUE = 'none';

    /** 排序选项 */
    export const SORT_DIRECTION_OPTIONS = [
        { value: SortDirection.Asc, label: '升序' },
        { value: SortDirection.Desc, label: '降序' },
    ];

    /** 基础视图元数据 */
    type TViewItem = {
        type: ViewMode;
        name: string;
        icon: string;
        color: string;
    };

    /** 氚云支持的视图集 */
    export const VIEW_MAP: Record<string, TViewItem> = {
        [ViewMode.List]: {
            type: ViewMode.List,
            name: '列表视图',
            icon: 'table-view-stroke',
            color: '#107FFF',
        },
        [ViewMode.Calendar]: {
            type: ViewMode.Calendar,
            name: '日历视图',
            icon: 'calendar-view-stroke',
            color: '#10C970',
        },
        [ViewMode.Timeline]: {
            type: ViewMode.Timeline,
            name: '时间轴',
            icon: 'timeline-stroke',
            color: '#00B8EE',
        },
        [ViewMode.Gantt]: {
            type: ViewMode.Gantt,
            name: '甘特图',
            icon: 'gantt-chart-stroke',
            color: '#FFA400',
        },
        [ViewMode.Map]: {
            type: ViewMode.Map,
            name: '地图视图',
            icon: 'map-view-stroke',
            color: '#0FC6B5',
        },
        [ViewMode.Hierarchy]: {
            type: ViewMode.Hierarchy,
            name: '层级视图',
            icon: 'hierarchy-view-stroke',
            color: '#FF5DA2',
        },
        [ViewMode.Card]: {
            type: ViewMode.Card,
            name: '卡片视图',
            icon: 'card-view-stroke',
            color: '#B357FF',
        },
        [ViewMode.Kanban]: {
            type: ViewMode.Kanban,
            name: '看板视图',
            icon: 'kanban-view-stroke',
            color: '#10C970',
        },
        [ViewMode.MobileGrid]: {
            type: ViewMode.MobileGrid,
            name: '表格视图',
            icon: 'table-sub-stroke',
            color: '#107FFF',
        },
        [ViewMode.Form]: {
            type: ViewMode.Form,
            name: '表单视图',
            icon: 'log-stoke',
            color: '#107fff',
        },
    };

    /** 日历视图时间模式选项 */
    export const CALENDAR_TIME_MODE_OPTIONS = [
        { label: '月', value: CalendarTimeMode.Month },
        { label: '周', value: CalendarTimeMode.Week },
        { label: '日', value: CalendarTimeMode.Day },
    ];

    /** 日历视图时间模式 */
    export const CalendarViewMode: Record<CalendarTimeMode, CalendarViewType> = {
        [CalendarTimeMode.Day]: CalendarViewType.DAY_VIEW,
        [CalendarTimeMode.Week]: CalendarViewType.WEEK_VIEW,
        [CalendarTimeMode.Month]: CalendarViewType.MONTH_VIEW,
    };

    /** 甘特图视图时间刻度选项 */
    export const GANTT_SCALE_TYPE_OPTIONS = [
        { label: '时', value: GanttScaleType.Hour },
        { label: '日', value: GanttScaleType.Day },
        { label: '周', value: GanttScaleType.Week },
        { label: '月', value: GanttScaleType.Month },
        { label: '季', value: GanttScaleType.Quarter },
        { label: '年', value: GanttScaleType.Year },
    ];

    /** 视图行高值(列表视图、层级视图) */
    export const ViewRowHeightValue = {
        [ViewRowHeight.Compact]: 36,
        [ViewRowHeight.Default]: 36,
        [ViewRowHeight.Medium]: 64,
        [ViewRowHeight.Large]: 92,
        [ViewRowHeight.HIGH]: 120,
    };

    /** options */
    export const ViewRowHeightOptions = [
        // 152迭代去除
        // { label: '紧凑', value: ViewRowHeight.Compact, icon: 'line-height-compact-stroke' },
        { label: '低', value: ViewRowHeight.Default, icon: 'line-height-compact-stroke' },
        { label: '中等', value: ViewRowHeight.Medium, icon: 'line-height-default-stroke' },
        { label: '高', value: ViewRowHeight.Large, icon: 'line-height-medium-stroke' },
        { label: '超高', value: ViewRowHeight.HIGH, icon: 'line-height-large-stroke' },
    ];

    /** 视图运行时命名空间 */
    export namespace Runtime {
        /** 视图渲染场景 */
        export enum ViewScene {
            /** 普通场景 */
            Normal = 'Normal',
            /** 筛选关联表单控件打开列表 */
            FilterQuery = 'FilterQuery',
            /** 关联列表 */
            AssociationList = 'AssociationList',
            /** 表单关联表单控件打开列表 */
            FormQuery = 'FormQuery',
        }

        /** 普通场景视图参数 */
        export type NormalSceneOptions = {
            /** 应用编码 */
            appCode: string;
            /** 表单编码 */
            schemaCode?: string;
            /** 是否是体验模式 */
            isExperienceMode?: boolean;
            /** 额外的查询条件 */
            extraQuery?: Record<string, string>;
        };

        /** 筛选关联表单控件打开列表场景视图参数 */
        export type FilterQuerySceneOptions = {
            /** 获取级联筛选数据的方法 */
            getCascaderFilterData?: () => Record<
                string,
                { operator: BizData.Filter.ComparisonOperatorType; value: unknown }
            >;
        };

        /** 关联列表场景视图参数 */
        export type AssociationListSceneOptions = {
            /** 关联列表场景传入源表单编码 */
            sourceSchemaCode: string;
            /** 关联列表场景传入源表单数据id */
            sourceBizObjectId: string;
            /** 关联字段 */
            mappingProperty: string;
            /** 视图标签页id */
            viewPageId: string | null;
            /** 是否是默认的显示字段 */
            isDefaultDisplayField: boolean;
            /** 缓存的筛选数据 */
            cachedFilterData?: Record<string, unknown[]>;
        };

        /** 表单关联表单控件打开列表场景视图参数 */
        export type FormQuerySceneOptions = {
            /** 源表单编码 */
            sourceSchemaCode: string;
            /** 源关联表单字段 */
            propertyName: string;
            /** 是否默认列配置 */
            isDefaultColumn: boolean;
            /** 获取数据限定范围筛选参数方法 */
            getAssociationFilterData?: () => BizData.Filter.IMatcherFilter['expressionFilter'];
        };

        /** 不同场景打开表单传递的参数 */
        export type ViewSceneOptions =
            | NormalSceneOptions
            | FilterQuerySceneOptions
            | AssociationListSceneOptions
            | FormQuerySceneOptions;

        /** 运行时视图配置 */
        export type ViewSetting = {
            /** 列表视图配置 */
            list: IListViewSetting;
            /** 日历视图配置 */
            calendar?: ICalendarViewSetting | null;
            /** 时间线视图配置 */
            timeline?: ITimelineViewSetting | null;
            /** 地图视图配置 */
            map?: IMapViewSetting | null;
            /** 甘特图视图配置 */
            gantt?: IGanttViewSetting | null;
            /** 卡片视图配置 */
            card?: ICardViewSetting | null;
            /** 层级视图配置 */
            hierarchy?: IHierarchyViewSetting | null;
            /** 卡片视图配置 */
            kanban?: IKanbanViewSetting | null;
        };

        /** 运行时不同场景功能配置 */
        export type TFunctionSwitch = {
            /** 是否显示功能按钮 */
            action: boolean;
            /** 是否允许新增 */
            creatable: boolean;
            /** 新增的数据是否默认选中 */
            defaultSelectCreated: boolean;
            /** 是否显示筛选条件 */
            filter: boolean;
            /** 筛选条件是否支持默认值 */
            defaultFilter: boolean;
            /** 是否支持编辑 */
            edit: boolean;
            /** 是否支持统计分析 */
            analysis: boolean;
            /** 是否支持视图列表切换 */
            viewList: boolean;
            /** 是否支持偏好 */
            preference: boolean;
            /** 是否支持配置列显示 */
            configColumn: boolean;
            /** 配置列时是否允许重新排序列 */
            reorderColumn: boolean;
            /** 是否支持切换子表 */
            switchGrid: boolean;
            /** 是否显示默认子表 */
            showDefaultGrid: boolean;
            /** 是否缓存个性化配置 */
            cachePersonalization: boolean;
            /** 是否支持列统计 */
            summary: boolean;
            /** 是否支持额外列配置 */
            extraColumn: boolean;
            /** 是否支持多视图(关联表单/关联列表等场景不支持) */
            multiView: boolean;
            /** 是否支持aiAssign */
            aiAssign: boolean;
        };

        export type TGlobalSwitch = {
            /** 是否开启层级视图 */
            [Operate.FunctionSwitchNameEnum.HierarchyEnabled]: boolean;
            /** 是否开启卡片视图 */
            [Operate.FunctionSwitchNameEnum.CardViewEnabled]: boolean;
            /** 是否开启看板视图 */
            [Operate.FunctionSwitchNameEnum.KanbanViewEnabled]: boolean;
            /** 是否开启筛选操作符 */
            [Operate.FunctionSwitchNameEnum.FilterOperatorEnable]: boolean;
            /** 是否开启多视图 */
            [Operate.FunctionSwitchNameEnum.MultiViewEnable]: boolean;
            /** 是否开启AI功能 */
            [Operate.FunctionSwitchNameEnum.OpenAIEnabled]: boolean;
            /** 是否是降级用户 */
            [Operate.FunctionSwitchNameEnum.IsDowngraded]?: boolean;
            /** 是否开启数据过滤功能 */
            [Operate.FunctionSwitchNameEnum.ViewDataFilterEnable]?: boolean;
        };

        export namespace Request {
            export interface IGetViewSettingModel {
                /** 表单编码 */
                schemaCode: string;
                /** 请求时间戳，用于判断是否需要使用缓存 */
                timestamp: number;
                /** pageId */
                viewPageId?: string;
            }
            export interface IGetViewPreferenceModel {
                /** 表单编码 */
                schemaCode: string;
                /** pageId */
                viewPageId?: string;
            }
            export interface IAddViewPreferenceModel {
                /** pageId */
                viewPageId?: string;
                /** 表单编码 */
                schemaCode: string;
                /** 名称 */
                title: string;
                /** 范围类型 */
                scopeType: unknown;
                /** 配置类型 */
                settingType: unknown;
                /** 配置 */
                config: unknown;
                /** 是否显示 */
                visible?: boolean;
                /** 字段 */
                fields?: string[];
                /** 视图筛选条件 */
                filterCriteria?: FilterCriterion[];
            }
            export interface IUpdateViewPreferenceModel {
                /** 表单编码 */
                schemaCode: string;
                /** 名称 */
                title: string;
                /** 范围类型 */
                scopeType: unknown;
                /** 配置类型 */
                settingType: unknown;
                /** 配置 */
                config: unknown;
                /** id */
                id: string;
                /** pageId */
                viewPageId: string;
                /** 是否显示 */
                visible?: boolean;
                /** 字段 */
                fields?: string[];
                /** 视图筛选条件 */
                filterCriteria?: FilterCriterion[];
            }
            export interface IRemoveViewPreferenceModel {
                /** 表单编码 */
                schemaCode: string;
                id: string;
                viewPageId: string;
            }
            export interface ISortViewPreferenceModel {
                /** 表单编码 */
                schemaCode: string;
                ids: string[];
                viewPageId: string;
            }
        }

        export namespace Response {
            /** 视图加载数据中的文件字段值类型 */
            export type TLoadDataFile = {
                /** 文件id */
                FileId: string;
                /** 文件名 */
                FileName: string;
                /** 文件大小 */
                FileSize: string;
                /** 文件id */
                ObjectId: string;
                /** 文件缩略图 */
                ThumbnailUrl: string;
                /** 文件地址 */
                Url: string;
                /** 文件类型（MIME） */
                ContentType: string;
                /** 文件描述 */
                Description?: string;
                /** 复制出来的文件对应的源文件的FileId */
                SourceFileId?: string;
            };

            /** 设计时校验是否可以开启层级视图 */
            export type TCheckHierarchyViewField = {
                isOverLimitCount: boolean;
                items: Array<{
                    propertyName: string;
                    /** 是否超层级 */
                    isOverReferenceLevel: boolean;
                    /** 是否为循环引用字段 */
                    isCircularReference: boolean;
                }>;
            };

            export interface IBaseViewSettingPage {
                /** 权限 */
                fieldAcls: IFieldPermission[];
                /** 视图按钮 */
                actions: IAction[];
                /** 是否放弃表单权限(当false时取的表单字段权限,当true时取视图字段权限-editable和required还是表单字段权限) */
                ignoreFieldAcl: boolean;
                /** 是否支持批量操作 */
                batchEnabled: boolean;
                /** 默认视图模式 */
                defaultViewMode: {
                    /** PC端默认视图模式 */
                    pc: ViewMode;
                    /** 移动端默认视图模式 */
                    mobile: ViewMode;
                };
                /** 可选视图模式 */
                optionalDisplayMode: ViewMode[];
                /** 筛选条件默认值 */
                queryDefaultValues: IQueryValueItem[];
                /** 筛选条件 */
                queryItems: IQueryItem[];
                /** 分组筛选 */
                groupQueryItems: IGroupQueryItem[];
                /** 默认的分组筛选 */
                defaultGroupQueryItem?: string | null;
                /** 日历视图配置 */
                calendar: ICalendarViewSetting | null;
                /** 甘特图视图配置 */
                gantt: IGanttViewSetting | null;
                /** 列表视图配置 */
                list: IListViewSetting;
                /** 地图视图配置 */
                map: IMapViewSetting | null;
                /** 时间轴视图配置 */
                timeline: ITimelineViewSetting | null;
                /** 卡片视图配置 */
                card: ICardViewSetting | null;
                /** 层级视图配置 */
                hierarchy: IHierarchyViewSetting | null;
                /** 看板视图配置 */
                kanban: IKanbanViewSetting | null;
                /** 表单视图配置 */
                formView: IFormViewSetting | null;
                /** 缓存时间戳 */
                timestamp?: number;
                /** id */
                viewPageId: string;
                /** 显示名称 */
                displayName?: string;
                /** 是否是主视图 */
                isDefault: boolean;
                /** 是否加载过 */
                loaded?: boolean;
                /** 后端自定义代码(debugTrack返回) */
                behindCode?: string | null;
                /** 视图前端自定义代码(debugTrack返回) */
                javascript?: string | null;
                /**
                 * 操作列配置
                 */
                operationColumnActions: Record<string, IAction[]>;
                /** 自定义代码列表配置 */
                customViewSetting?: string | null;
            }

            export interface IMainViewSettingPage extends IBaseViewSettingPage {
                /** 后端自定义代码 */
                behindCode: string | null;
                /** 视图前端自定义代码 */
                javascript: string | null;
                /** 自定义代码调试栈信息 */
                debugTrack: CustomCode.IDebugTrack | null;
            }

            /** 视图标签页信息 */
            export interface IRuntimeViewSettingPage {
                /** id */
                pageId: string;
                /** 显示名称 */
                displayName: string;
                /** 是否是主视图 */
                isDefault: boolean;
                /** 排序 */
                sortKey: number;
            }

            /** 视图配置 */
            export interface IRuntimeViewSetting {
                /** 当前用户支持的数据查看范围 */
                scopeTypes: Array<{ key: Auth.DataScopeType; value: string }>;
                /** 后端自定义代码 */
                behindCode: string | null;
                /** 视图前端自定义代码 */
                javascript: string | null;
                /** 自定义代码调试栈信息 */
                debugTrack: CustomCode.IDebugTrack | null;
                /** pages */
                pages: IBaseViewSettingPage[];
                /** 缓存时间戳 */
                timestamp?: number;
            }
        }
    }

    /** 视图设计时命名空间 */
    export namespace ViewDesigner {
        /** 视图设计器公共校验状态 */
        export enum ValidateStatus {
            Success = 'success',
            Warning = 'warning',
            Error = 'error',
        }

        /** 代码类型 */
        export enum CodeType {
            Js,
            Behind,
        }

        /** 运行时不同场景功能配置 */
        export type TGlobalSwitch = {
            /** 是否开启层级视图 */
            [Operate.FunctionSwitchNameEnum.HierarchyEnabled]: boolean;
            /** 是否开启卡片视图 */
            [Operate.FunctionSwitchNameEnum.CardViewEnabled]: boolean;
            /** 是否开启看板视图 */
            [Operate.FunctionSwitchNameEnum.KanbanViewEnabled]: boolean;
            /** 是否开启筛选操作符 */
            [Operate.FunctionSwitchNameEnum.FilterOperatorEnable]: boolean;
            /** 是否开启多视图 */
            [Operate.FunctionSwitchNameEnum.MultiViewEnable]: boolean;
            /** 是否为降级用户 */
            [Operate.FunctionSwitchNameEnum.IsDowngraded]?: boolean;
            /** 是否开启数据过滤功能 */
            [Operate.FunctionSwitchNameEnum.ViewDataFilterEnable]?: boolean;
        };

        /** 校验结果 */
        export type ValidateResult = {
            /** 校验状态 */
            status: ValidateStatus;
            /** 提示信息 */
            msg: string;
            /** key */
            key?: IViewPropertyKey;
        };

        /** 配置key(视图-属性) */
        export enum IViewPropertyKey {
            /** 筛选 */
            Filter = 'filter',
            /** 分组筛选 */
            GroupFilter = 'groupFilter',
            /** 数据过滤 */
            DataFilter = 'dataFilter',
            /** 功能按钮 */
            EnableAction = 'enableAction',
            /** 开启视图 */
            View = 'view',
            /** 开启的视图 */
            OptionalDisplayMode = 'optionalDisplayMode',
            /** 默认显示 */
            DefaultViewMode = 'defaultViewMode',
            /** 默认显示-电脑端 */
            DefaultViewModePC = 'defaultViewMode-pc',
            /** 默认显示-手机端 */
            DefaultViewModeMobile = 'defaultViewMode-mobile',
            /** 权限配置 */
            EnableColumnAcl = 'enableColumnAcl',
            /** 批量操作 */
            BatchEnabled = 'batchEnabled',
            /** 筛选-显示字段 */
            FilterFieldDisplay = 'filter-fieldDisplay',
            /** 分组筛选-显示字段 */
            GroupFilterFieldDisplay = 'groupFilter-fieldDisplay',
            /** 数据过滤-显示字段 */
            DataFilterFieldDisplay = 'dataFilter-fieldDisplay',
            /** 列表视图 */
            List = 'list',
            /** 列表-默认显示子表 */
            ListDefaultDisplayChildField = 'list-defaultDisplayChildField',
            /** 操作列，目前只有列表视图和卡片视图支持 */
            ListOperationColumn = 'list-operationColumn',
            /** 列表-列表图片 */
            ListIconField = 'list-iconField',
            /** 列表-默认排序规则 */
            ListSortByCollection = 'list-sortByCollection',
            /** 列表-排序 */
            ListSort = 'list-sort',
            /** 列表-排序字段 */
            ListSortBy = 'list-sortBy',
            /** 列表-排序方式 */
            ListSortDirection = 'list-sortDirection',
            /** 列表-显示字段 */
            ListDisplayProperties = 'list-displayProperties',
            /** 列表-默认行高 */
            ListDefaultRowHeight = 'list-defaultRowHeight',
            /** 日历视图 */
            Calendar = 'calendar',
            /** 日历-时间跨度 */
            CalendarRange = 'calendar-range',
            /** 日历-开始日期字段 */
            CalendarStartField = 'calendar-startField',
            /** 日历-结束日期字段 */
            CalendarEndField = 'calendar-endField',
            /** 日历-默认展示 */
            CalendarTimeMode = 'calendar-timeMode',
            /** 甘特图视图 */
            Gantt = 'gantt',
            /** 甘特图-时间跨度 */
            GanttRange = 'gantt-range',
            /** 甘特图-开始日期字段 */
            GanttStartField = 'gantt-startField',
            /** 甘特图-结束日期字段 */
            GanttEndField = 'gantt-endField',
            /** 甘特图-层级关系 */
            GanttLevelRelation = 'gantt-levelRelation',
            /** 甘特图-依赖关系 */
            GanttDependency = 'gantt-dependency',
            /** 甘特图-甘特图刻度 */
            GanttGanttScale = 'gantt-ganttScale',
            /** 甘特图-进度字段 */
            GanttProgressField = 'gantt-progressField',
            /** 甘特图-颜色规则 */
            GanttColorRules = 'gantt-colorRules',
            /** 甘特图-排序 */
            GanttSort = 'gantt-sort',
            /** 甘特图-排序字段 */
            GanttSortBy = 'gantt-sortBy',
            /** 甘特图-排序方式 */
            GanttSortDirection = 'gantt-sortDirection',
            /** 甘特图-列权限 */
            GanttPropertiesAuths = 'gantt-propertiesAuths',
            /** 时间轴视图 */
            Timeline = 'timeline',
            /** 时间轴-位置字段 */
            TimelineTimelineAxis = 'timeline-timelineAxis',
            /** 时间轴-排序字段 */
            TimelineSortDirection = 'timeline-sortDirection',
            /** 地图视图 */
            Map = 'map',
            /** 地图-位置字段 */
            MapLocationField = 'map-locationField',
            /** 地图-移动端限制范围 */
            MapMobileNearby = 'map-mobileNearby',
            /** 地图-是否限制移动端数据展示范围 */
            MapIsMobileNearbyData = 'map-isMobileNearbyData',
            /** 地图-移动端数据展示范围 */
            MapIsMobileNearbyDataRangeInMeters = 'map-isMobileNearbyDataRangeInMeters',
            /** 地图-移动端是否按位置字段排序 */
            MapIsMobileSortByLocation = 'map-isMobileSortByLocation',
            /** 地图-排序 */
            MapSort = 'map-sort',
            /** 地图-排序字段 */
            MapSortBy = 'map-sortBy',
            /** 地图-排序方式 */
            MapSortDirection = 'map-sortDirection',
            /** 层级视图 */
            Hierarchy = 'hierarchy',
            /** 层级视图-排序 */
            HierarchySort = 'hierarchy-sort',
            /** 层级视图-排序字段 */
            HierarchySortKey = 'hierarchy-sortKey',
            /** 层级视图-排序方向 */
            HierarchySortDirection = 'hierarchy-sortDirection',
            /** 层级视图-层级字段 */
            HierarchyField = 'hierarchy-hierarchyField',
            /** 层级视图-显示方式 */
            HierarchyCss = 'hierarchy-css',
            /** 层级视图-显示字段 */
            HierarchyDisplayProperties = 'hierarchy-displayProperties',
            /** 层级视图-默认行高 */
            HierarchyDefaultRowHeight = 'hierarchy-defaultRowHeight',
            /** 卡片视图 */
            Card = 'card',
            /** 卡片视图-排序 */
            CardSort = 'card-sort',
            /** 卡片视图-排序字段 */
            CardSortKey = 'card-sortKey',
            /** 卡片视图-排序方向 */
            CardSortDirection = 'card-sortDirection',
            /** 卡片视图-图片字段 */
            CardImagePropertyName = 'card-imagePropertyName',
            /** 卡片视图-图片位置 */
            CardImagePosition = 'card-imagePosition',
            /** 卡片视图-图片显示方式 */
            CardImageDisplay = 'card-imageDisplay',
            /** 卡片视图-显示字段 */
            CardDisplayProperties = 'card-displayProperties',
            /** 卡片视图-是否隐藏名称 */
            CardHideDisplayName = 'card-hideDisplayName',
            /** 卡片视图-操作列 */
            CardOperationColumn = 'card-operationColumn',
            /** 看板视图 */
            Kanban = 'kanban',
            /** 看板视图-排序 */
            KanbanSort = 'kanban-sort',
            /** 看板视图-排序字段 */
            KanbanSortKey = 'kanban-sortKey',
            /** 看板视图-排序方向 */
            KanbanSortDirection = 'kanban-sortDirection',
            /** 看板视图-图片字段 */
            KanbanImagePropertyName = 'kanban-imagePropertyName',
            /** 看板视图-图片位置 */
            KanbanImagePosition = 'kanban-imagePosition',
            /** 看板视图-图片显示方式 */
            KanbanImageDisplay = 'kanban-imageDisplay',
            /** 看板视图-显示字段 */
            KanbanDisplayProperties = 'kanban-displayProperties',
            /** 看板视图-是否隐藏名称 */
            KanbanHideDisplayName = 'kanban-hideDisplayName',
            /** 看板视图-分组字段 */
            KanbanGroupKey = 'kanban-groupKey',
            /** 移动端表格视图 */
            MobileGrid = 'mobile-grid',
            /** 表单视图 */
            Form = 'form',
            /** 表单视图-左侧列表 */
            FormLeftList = 'form-leftList',
            /** 表单视图-是否开启左侧列表 */
            FormLeftListOpen = 'form-leftListOpen',
            /** 表单视图-显示字段 */
            FormDisplayProperties = 'form-displayProperties',
            /** 表单视图-图片字段 */
            FormImagePropertyName = 'form-imagePropertyName',
            /** 表单视图-图片显示方式 */
            FormImageDisplay = 'form-imageDisplay',
            /** 表单视图-排序 */
            FormSort = 'form-sort',
            /** 表单视图-排序字段 */
            FormSortKey = 'form-sortKey',
            /** 表单视图-排序方向 */
            FormSortDirection = 'form-sortDirection',
        }

        /** 设计器配置项类型 */
        export type IDesignConfigOption = {
            /** 配置key */
            key: IViewPropertyKey;
            /** 标题 */
            label: string;
            /** 提示语 */
            tip?: string;
            /** 是否隐藏 */
            hide?: boolean;
            /** 是否需要开关展示隐藏 */
            configurable?: boolean;
            /** 配置项 */
            options?: Array<{ label: string; value: string | SortDirection | number }>;
            /** 占位符 */
            placeholder?: string;
            /** 元素id，用于功能引导查找对应元素 */
            elementId?: string;
        };

        /** 视图配置 */
        export type IViewSettingConfig =
            | IListViewSetting
            | ICalendarViewSetting
            | IMapViewSetting
            | ITimelineViewSetting
            | IGanttViewSetting
            | ICardViewSetting
            | IHierarchyViewSetting
            | IFormViewSetting
            | {
                  defaultViewMode?: { pc: ViewMode; mobile: ViewMode };
                  batchEnabled?: boolean;
                  optionalDisplayMode?: ViewMode[];
              };

        /** 视图列 */
        export type IViewColumn = {
            code: string;
            children?: IViewColumn[];
        };

        /** 设计态视图配置(前端) */
        export interface IDesignerViewSetting {
            /** 后端自定义代码 */
            behindCode: string | null;
            /** 前端自定义代码 */
            javascript: string | null;
            /** pages */
            pages: IDesignerViewPageSetting[];
        }

        /** 设计态视图配置(前端) */
        export interface IDesignerViewPageSetting {
            /** id */
            viewPageId?: string;
            /** 是否是主视图 */
            isDefault: boolean;
            /** 显示名称 */
            displayName?: string;
            /** 排序 */
            sortKey?: number;
            /** 视图按钮 */
            // actions: IAction[];
            smartActionCodes: string[];
            /** 是否支持批量操作 */
            batchEnabled: boolean;
            /** 视图字段所有用户可见 */
            enableColumnAcl: boolean;
            /** 默认视图模式 */
            defaultViewMode: {
                /** PC端默认视图模式 */
                pc: ViewMode;
                /** 移动端默认视图模式 */
                mobile: ViewMode;
            };
            /** 筛选条件 */
            queryItems: IQueryItem[];
            /** 分组筛选 */
            groupQueryItems: IGroupQueryItem[];
            /** 默认的分组筛选 */
            defaultGroupQueryItem?: string | null;
            /** 展示的视图 */
            optionalDisplayMode: ViewMode[];
            /** 列表视图配置 */
            list: IListViewSetting;
            /** 日历视图配置 */
            calendar: ICalendarViewSetting | null;
            /** 甘特图视图配置 */
            gantt: IGanttViewSetting | null;
            /** 地图视图配置 */
            map: IMapViewSetting | null;
            /** 时间轴视图配置 */
            timeline: ITimelineViewSetting | null;
            /** 卡片视图配置 */
            card: ICardViewSetting | null;
            /** 层级视图配置 */
            hierarchy: IHierarchyViewSetting | null;
            /** 看板视图配置 */
            kanban: IKanbanViewSetting | null;
            /** 表单视图配置 */
            formView: IFormViewSetting | null;
            /** 自定义代码列表页视图配置 */
            customViewSetting: string | null;
        }

        export interface IDesignerViewModel extends IDesignerViewPageSetting {
            /** 后端自定义代码 */
            behindCode: string | null;
            /** 前端自定义代码 */
            javascript: string | null;
        }

        export namespace Request {
            export interface ISaveDesignerViewModel {
                schemaCode: string;
                viewSettings: IDesignerViewModel[];
            }

            // 单独保存列表设计器按钮
            export interface ISaveDesignerViewActionsModel {
                schemaCode: string;
                actions: IAction[];
                viewPageId?: string; // 页面Id
            }
        }

        export namespace Response {
            export interface ILoadDesignerViewModel extends IDesignerViewModel {
                /** id */
                objectId: string;
            }
        }
    }
}
